import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Edit, Trash2, Box, LayoutGrid, ChevronDown, ChevronRight, Layers, MapPin, Package, Printer } from "lucide-react";

const HubLayoutPage = ({ user, onLogout }) => {
  const { hubId } = useParams();
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingLayout, setEditingLayout] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    location_name: "", dimension_l: "", dimension_b: "", dimension_h: "", weight_capacity_kg: ""
  });

  // Sublocation management
  const [expandedLayout, setExpandedLayout] = useState(null);
  const [showSubDialog, setShowSubDialog] = useState(false);
  const [subForm, setSubForm] = useState({ name: "", sequence: "" });
  const [editingSub, setEditingSub] = useState(null);
  const [selectedLayoutId, setSelectedLayoutId] = useState(null);
  const [savingSub, setSavingSub] = useState(false);

  // Bulk sublocation generation
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkForm, setBulkForm] = useState({ prefix: "", start: 1, end: 50 });

  useEffect(() => {
    fetchHub();
    fetchLayouts();
  }, [hubId]);

  const fetchHub = async () => {
    try {
      const res = await axios.get(`${API}/hubs/${hubId}`);
      setHub(res.data);
    } catch (e) {
      toast.error("Hub not found");
      navigate("/pharmeasy/hub-ops");
    }
  };

  const fetchLayouts = async () => {
    try {
      const res = await axios.get(`${API}/hubs/${hubId}/layouts`);
      setLayouts(res.data);
    } catch (e) { toast.error("Failed to fetch layouts"); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditingLayout(null);
    setForm({ location_name: "", dimension_l: "", dimension_b: "", dimension_h: "", weight_capacity_kg: "" });
    setShowDialog(true);
  };

  const openEdit = (layout) => {
    setEditingLayout(layout);
    setForm({
      location_name: layout.location_name || "",
      dimension_l: layout.dimension_l?.toString() || "",
      dimension_b: layout.dimension_b?.toString() || "",
      dimension_h: layout.dimension_h?.toString() || "",
      weight_capacity_kg: layout.weight_capacity_kg?.toString() || "",
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location_name) { toast.error("Location name is required"); return; }
    const payload = {
      location_name: form.location_name,
      dimension_l: parseFloat(form.dimension_l) || 0,
      dimension_b: parseFloat(form.dimension_b) || 0,
      dimension_h: parseFloat(form.dimension_h) || 0,
      weight_capacity_kg: parseFloat(form.weight_capacity_kg) || 0,
    };
    setSaving(true);
    try {
      if (editingLayout) {
        await axios.put(`${API}/hubs/${hubId}/layouts/${editingLayout._id}`, payload);
        toast.success("Layout updated");
      } else {
        await axios.post(`${API}/hubs/${hubId}/layouts`, payload);
        toast.success("Layout added");
      }
      setShowDialog(false);
      fetchLayouts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save layout");
    } finally { setSaving(false); }
  };

  const handleDelete = async (layoutId) => {
    if (!window.confirm("Delete this layout location?")) return;
    try {
      await axios.delete(`${API}/hubs/${hubId}/layouts/${layoutId}`);
      toast.success("Layout deleted");
      fetchLayouts();
    } catch (err) { toast.error("Failed to delete layout"); }
  };

  // Sublocation handlers
  const toggleExpand = (layoutId) => {
    setExpandedLayout(expandedLayout === layoutId ? null : layoutId);
  };

  const openAddSub = (layoutId) => {
    setSelectedLayoutId(layoutId);
    setEditingSub(null);
    const layout = layouts.find(l => l._id === layoutId);
    const subs = layout?.sublocations || [];
    const nextSeq = subs.length > 0 ? Math.max(...subs.map(s => s.sequence)) + 1 : 1;
    setSubForm({ name: `Slot ${nextSeq}`, sequence: nextSeq.toString() });
    setShowSubDialog(true);
  };

  const openEditSub = (layoutId, sub) => {
    setSelectedLayoutId(layoutId);
    setEditingSub(sub);
    setSubForm({ name: sub.name, sequence: sub.sequence.toString() });
    setShowSubDialog(true);
  };

  const handleSubSubmit = async (e) => {
    e.preventDefault();
    if (!subForm.name || !subForm.sequence) { toast.error("Name and sequence are required"); return; }
    setSavingSub(true);
    try {
      const payload = { name: subForm.name, sequence: parseInt(subForm.sequence) };
      if (editingSub) {
        await axios.put(`${API}/hubs/${hubId}/layouts/${selectedLayoutId}/sublocations/${editingSub.id}`, payload);
        toast.success("Sublocation updated");
      } else {
        await axios.post(`${API}/hubs/${hubId}/layouts/${selectedLayoutId}/sublocations`, payload);
        toast.success("Sublocation added");
      }
      setShowSubDialog(false);
      fetchLayouts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save sublocation");
    } finally { setSavingSub(false); }
  };

  const handleDeleteSub = async (layoutId, subId) => {
    if (!window.confirm("Delete this sublocation?")) return;
    try {
      await axios.delete(`${API}/hubs/${hubId}/layouts/${layoutId}/sublocations/${subId}`);
      toast.success("Sublocation deleted");
      fetchLayouts();
    } catch (err) { toast.error("Failed to delete sublocation"); }
  };

  // Bulk sublocation generation
  const openBulkDialog = (layoutId) => {
    setSelectedLayoutId(layoutId);
    setBulkForm({ prefix: "Slot", start: 1, end: 50 });
    setShowBulkDialog(true);
  };

  const handleBulkGenerate = async (e) => {
    e.preventDefault();
    const { prefix, start, end } = bulkForm;
    if (!prefix || start > end) { toast.error("Invalid range"); return; }
    
    const sublocations = [];
    for (let i = parseInt(start); i <= parseInt(end); i++) {
      sublocations.push({ name: `${prefix} ${i}`, sequence: i });
    }
    
    setSavingSub(true);
    try {
      const res = await axios.post(`${API}/hubs/${hubId}/layouts/${selectedLayoutId}/sublocations/bulk`, sublocations);
      toast.success(`Added ${res.data.added} sublocations (total: ${res.data.total})`);
      setShowBulkDialog(false);
      fetchLayouts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate sublocations");
    } finally { setSavingSub(false); }
  };

  // Download sticker PDF for a location
  const downloadStickers = async (layoutId, locationName) => {
    try {
      toast.info("Generating stickers PDF...");
      const response = await axios.get(`${API}/hubs/${hubId}/layouts/${layoutId}/generate-stickers`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${locationName}_stickers.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Stickers PDF downloaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate stickers PDF");
    }
  };

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="hub-layout-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate("/pharmeasy/hub-ops")} data-testid="back-to-hubs-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Hubs
            </Button>
            <div>
              <h1 className="text-4xl font-bold text-slate-800">{hub?.hub_name || "Hub"} — Layout</h1>
              <p className="text-slate-600 mt-1">{hub?.address ? `${hub.city || ''}, ${hub.state || ''}` : "Loading..."}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(`/pharmeasy/hub-ops/${hubId}/inward`)} data-testid="inward-btn">
              <Package className="w-4 h-4 mr-2" /> Inward
            </Button>
            <Button onClick={openAdd} style={{ backgroundColor: "#1B8C3D" }} data-testid="add-layout-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Location
            </Button>
          </div>
        </div>

        {/* Layout Table */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-600">Loading layouts...</div>
            ) : layouts.length === 0 ? (
              <div className="p-12 text-center">
                <Box className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-500 mb-1">No layout locations added yet</p>
                <p className="text-sm text-slate-400 mb-4">Add zones, racks, or storage areas for this hub</p>
                <Button onClick={openAdd} style={{ backgroundColor: "#1B8C3D" }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Location
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap w-10"></th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Location Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Dimensions (L×B×H)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Weight Capacity</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Sublocations</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {layouts.map((layout) => (
                      <>
                        <tr key={layout._id} className="hover:bg-slate-50" data-testid={`layout-row-${layout._id}`}>
                          <td className="px-4 py-4 align-middle">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="p-1 h-auto"
                              onClick={() => toggleExpand(layout._id)}
                            >
                              {expandedLayout === layout._id ? (
                                <ChevronDown className="w-4 h-4 text-slate-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-500" />
                              )}
                            </Button>
                          </td>
                          <td className="px-4 py-4 align-middle">
                            <div className="flex items-center gap-2">
                              <LayoutGrid className="w-4 h-4 text-emerald-500" />
                              <span className="text-sm font-medium text-slate-900">{layout.location_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 align-middle text-sm text-slate-600">
                            {layout.dimension_l} × {layout.dimension_b} × {layout.dimension_h} ft
                          </td>
                          <td className="px-4 py-4 align-middle text-right text-sm font-medium text-slate-800">{layout.weight_capacity_kg} kg</td>
                          <td className="px-4 py-4 align-middle">
                            <Badge variant="outline" className="text-xs">
                              <Layers className="w-3 h-3 mr-1" />
                              {(layout.sublocations || []).length} slots
                            </Badge>
                          </td>
                          <td className="px-4 py-4 align-middle">
                            <div className="flex gap-1.5">
                              <Button size="sm" variant="outline" onClick={() => openEdit(layout)} data-testid={`edit-layout-${layout._id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete(layout._id)} data-testid={`delete-layout-${layout._id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded Sublocations */}
                        {expandedLayout === layout._id && (
                          <tr key={`${layout._id}-sub`}>
                            <td colSpan="6" className="bg-slate-50 px-4 py-4">
                              <div className="pl-8">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-slate-700">
                                    Sublocations / Slots
                                  </h4>
                                  <div className="flex gap-2">
                                    <Button size="sm" variant="outline" onClick={() => downloadStickers(layout._id, layout.location_name)}
                                      disabled={(layout.sublocations || []).length === 0}
                                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50">
                                      <Printer className="w-3 h-3 mr-1" /> Print Slot Barcode
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => openBulkDialog(layout._id)}>
                                      <Layers className="w-3 h-3 mr-1" /> Generate Bulk
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => openAddSub(layout._id)}>
                                      <Plus className="w-3 h-3 mr-1" /> Add Slot
                                    </Button>
                                  </div>
                                </div>
                                {(layout.sublocations || []).length === 0 ? (
                                  <p className="text-sm text-slate-400">No sublocations defined. Add slots for shipment assignment.</p>
                                ) : (
                                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                                    {(layout.sublocations || [])
                                      .sort((a, b) => a.sequence - b.sequence)
                                      .map((sub) => (
                                        <div 
                                          key={sub.id} 
                                          className="flex items-center justify-between bg-white border rounded-lg px-3 py-2 text-sm group hover:border-emerald-300"
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-semibold">
                                              {sub.sequence}
                                            </span>
                                            <span className="text-slate-700">{sub.name}</span>
                                          </div>
                                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                              className="text-slate-400 hover:text-blue-600"
                                              onClick={() => openEditSub(layout._id, sub)}
                                            >
                                              <Edit className="w-3 h-3" />
                                            </button>
                                            <button 
                                              className="text-slate-400 hover:text-red-600"
                                              onClick={() => handleDeleteSub(layout._id, sub.id)}
                                            >
                                              <Trash2 className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                )}
                                {/* Pincode History */}
                                {(layout.pincode_history || []).length > 0 && (
                                  <div className="mt-4 pt-3 border-t">
                                    <p className="text-xs font-semibold text-slate-500 mb-2">Historical Pincodes</p>
                                    <div className="flex flex-wrap gap-1">
                                      {(layout.pincode_history || []).map((ph) => (
                                        <Badge key={ph.pincode} variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                          <MapPin className="w-3 h-3 mr-1" />
                                          {ph.pincode} ({ph.assignment_count}x)
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Layout Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingLayout ? "Edit Layout Location" : "Add Layout Location"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div>
              <Label htmlFor="location_name">Location Name *</Label>
              <Input id="location_name" value={form.location_name}
                onChange={(e) => setForm({ ...form, location_name: e.target.value })}
                placeholder="e.g. Zone A - Rack 1" required className="mt-1" data-testid="layout-location-name-input" />
            </div>
            <div>
              <Label>Dimensions</Label>
              <div className="grid grid-cols-3 gap-3 mt-1">
                <div>
                  <span className="text-xs text-slate-500 mb-1 block">Length (ft)</span>
                  <Input type="number" step="0.01" value={form.dimension_l}
                    onChange={(e) => setForm({ ...form, dimension_l: e.target.value })}
                    placeholder="L" data-testid="layout-dimension-l-input" />
                </div>
                <div>
                  <span className="text-xs text-slate-500 mb-1 block">Breadth (ft)</span>
                  <Input type="number" step="0.01" value={form.dimension_b}
                    onChange={(e) => setForm({ ...form, dimension_b: e.target.value })}
                    placeholder="B" data-testid="layout-dimension-b-input" />
                </div>
                <div>
                  <span className="text-xs text-slate-500 mb-1 block">Height (ft)</span>
                  <Input type="number" step="0.01" value={form.dimension_h}
                    onChange={(e) => setForm({ ...form, dimension_h: e.target.value })}
                    placeholder="H" data-testid="layout-dimension-h-input" />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="weight_capacity_kg">Weight Capacity (kg)</Label>
              <Input id="weight_capacity_kg" type="number" step="0.01" value={form.weight_capacity_kg}
                onChange={(e) => setForm({ ...form, weight_capacity_kg: e.target.value })}
                placeholder="e.g. 500" className="mt-1" data-testid="layout-weight-capacity-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ backgroundColor: "#1B8C3D" }} data-testid="layout-save-btn">
                {saving ? "Saving..." : editingLayout ? "Update" : "Add Location"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Sublocation Dialog */}
      <Dialog open={showSubDialog} onOpenChange={setShowSubDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingSub ? "Edit Sublocation" : "Add Sublocation"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubSubmit} className="space-y-4 py-2">
            <div>
              <Label htmlFor="sub_name">Slot Name *</Label>
              <Input id="sub_name" value={subForm.name}
                onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
                placeholder="e.g. Slot 1" required className="mt-1" />
            </div>
            <div>
              <Label htmlFor="sub_sequence">Sequence Number *</Label>
              <Input id="sub_sequence" type="number" min="1" value={subForm.sequence}
                onChange={(e) => setSubForm({ ...subForm, sequence: e.target.value })}
                placeholder="e.g. 1" required className="mt-1" />
              <p className="text-xs text-slate-400 mt-1">Shipments are assigned to slots by delivery sequence</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSubDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={savingSub} style={{ backgroundColor: "#1B8C3D" }}>
                {savingSub ? "Saving..." : editingSub ? "Update" : "Add Slot"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Generate Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Generate Sublocations</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBulkGenerate} className="space-y-4 py-2">
            <div>
              <Label htmlFor="bulk_prefix">Name Prefix</Label>
              <Input id="bulk_prefix" value={bulkForm.prefix}
                onChange={(e) => setBulkForm({ ...bulkForm, prefix: e.target.value })}
                placeholder="e.g. Slot" required className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bulk_start">Start Sequence</Label>
                <Input id="bulk_start" type="number" min="1" value={bulkForm.start}
                  onChange={(e) => setBulkForm({ ...bulkForm, start: parseInt(e.target.value) || 1 })}
                  required className="mt-1" />
              </div>
              <div>
                <Label htmlFor="bulk_end">End Sequence</Label>
                <Input id="bulk_end" type="number" min="1" value={bulkForm.end}
                  onChange={(e) => setBulkForm({ ...bulkForm, end: parseInt(e.target.value) || 50 })}
                  required className="mt-1" />
              </div>
            </div>
            <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
              This will create {Math.max(0, bulkForm.end - bulkForm.start + 1)} sublocations: 
              "{bulkForm.prefix} {bulkForm.start}", "{bulkForm.prefix} {bulkForm.start + 1}", ... "{bulkForm.prefix} {bulkForm.end}"
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={savingSub} style={{ backgroundColor: "#1B8C3D" }}>
                {savingSub ? "Generating..." : "Generate"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PharmeasyLayout>
  );
};

export default HubLayoutPage;
